/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Button,
  Tooltip,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  CalendarToday as CalendarIcon,
  Inventory as InventoryIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import ResourceAssignmentEditDialog from './ResourceAssignmentEditDialog';
import { formatUsagePeriod } from '../../utils/usagePeriods';
import { formatRate } from '../../utils/ratePricing';

/**
 * Assignment Management Pane - New Schema Version
 * 
 * Displays chamber and resource assignments for a project.
 * Uses the new schema where assignments have rate_id instead of unit references.
 */
const AssignmentManagementPane = ({ application, onAssignChambers, onAssignResources, onAssignmentUpdate }) => {
  const [tabValue, setTabValue] = useState(0);
  const [chamberAssignments, setChamberAssignments] = useState([]);
  const [completedChamberAssignments, setCompletedChamberAssignments] = useState([]);
  const [resourceAssignments, setResourceAssignments] = useState([]);
  const [editingResource, setEditingResource] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [editForm, setEditForm] = useState({
    quantity: 0,
    rate_id: '',
    start_date: null,
    end_date: null,
    comment: ''
  });

  useEffect(() => {
    if (application) {
      fetchAssignments();
    }
  }, [application]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/projects/${application._id}/assignments`);
      
      if (response.data?.assignments) {
        const activeAssignments = [];
        const completedAssignments = [];
        
        response.data.assignments.forEach(ca => {
          const assignments = ca.assignments || [];
          const activeAssigns = assignments.filter(a => a.status === 'Active');
          const completedAssigns = assignments.filter(a => a.status === 'Completed' || a.status === 'Terminated');
          
          if (activeAssigns.length > 0) {
            activeAssignments.push({
              chamber: ca.chamber,
              assignments: activeAssigns.map(a => ({
                ...a,
                chamber_id: ca.chamber._id,
                chamber_name: ca.chamber.chamber
              }))
            });
          }
          
          if (completedAssigns.length > 0) {
            completedAssignments.push({
              chamber: ca.chamber,
              assignments: completedAssigns.map(a => ({
                ...a,
                chamber_id: ca.chamber._id,
                chamber_name: ca.chamber.chamber
              }))
            });
          }
        });
        
        setChamberAssignments(activeAssignments);
        setCompletedChamberAssignments(completedAssignments);
      } else {
        setChamberAssignments([]);
        setCompletedChamberAssignments([]);
      }
      
      try {
        const resRes = await axios.get(`/api/resources/assignments/project/${application._id}`);
        setResourceAssignments(resRes.data?.assignments || []);
      } catch (resErr) {
        console.error('Error fetching resource assignments:', resErr);
        setResourceAssignments([]);
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
      toast.error('Failed to fetch assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteAssignment = async (chamberId, assignmentId) => {
    try {
      const response = await axios.patch(`/api/projects/${application._id}/assignments/${assignmentId}/complete`, {
        end_date: new Date().toISOString(),
        status: 'Completed'
      });

      if (response.data) {
        toast.success('Assignment completed successfully');
        await fetchAssignments();
        
        setTimeout(() => {
          if (onAssignmentUpdate) {
            onAssignmentUpdate();
          }
        }, 500);
      }
    } catch (error) {
      console.error('Error completing assignment:', error);
      toast.error('Failed to complete assignment');
    }
  };

  const handleDeleteAssignment = async (chamberId, assignmentId, rateName) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the assignment for ${rateName}? This action cannot be undone.`
    );
    
    if (!confirmed) return;

    try {
      const response = await axios.delete(`/api/projects/${application._id}/assignments/${assignmentId}`);

      if (response.data) {
        toast.success('Assignment deleted successfully');
        await fetchAssignments();
        
        setTimeout(() => {
          if (onAssignmentUpdate) {
            onAssignmentUpdate();
          }
        }, 500);
      }
    } catch (error) {
      console.error('Error deleting assignment:', error);
      toast.error('Failed to delete assignment');
    }
  };

  // Open edit dialog
  const handleEditAssignment = (chamberAssign, assignment) => {
    setEditingAssignment({
      ...assignment,
      chamber: chamberAssign.chamber
    });
    
    // Parse dates from ISO string
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      const [datePart] = dateStr.toString().split('T');
      const [year, month, day] = datePart.split('-');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    };
    
    setEditForm({
      quantity: assignment.quantity,
      rate_id: assignment.rate_id,
      start_date: parseDate(assignment.start_date),
      end_date: parseDate(assignment.end_date),
      comment: assignment.comment || ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const endDate = editForm.end_date ? new Date(editForm.end_date) : null;
      if (endDate) endDate.setHours(0, 0, 0, 0);
      
      const isEndDatePast = endDate && endDate < today;
      const isCurrentlyCompleted = editingAssignment.status === 'Completed' || editingAssignment.status === 'Terminated';
      
      let newStatus;
      if (isEndDatePast && !isCurrentlyCompleted) {
        newStatus = 'Completed';
      } else if (!isEndDatePast && isCurrentlyCompleted) {
        newStatus = 'Active';
      }

      const updateData = {
        quantity: parseFloat(editForm.quantity),
        rate_id: editForm.rate_id,
        start_date: editForm.start_date ? editForm.start_date.toISOString() : null,
        end_date: editForm.end_date ? editForm.end_date.toISOString() : null,
        comment: editForm.comment?.trim() || null,
        ...(newStatus && { status: newStatus })
      };

      const response = await axios.put(
        `/api/projects/${application._id}/assignments/${editingAssignment._id}`,
        updateData
      );

      if (response.data) {
        if (newStatus === 'Completed') {
          toast.success('Assignment updated and moved to completed');
        } else if (newStatus === 'Active') {
          toast.success('Assignment updated and moved to current');
        } else {
          toast.success('Assignment updated successfully');
        }
        setEditDialogOpen(false);
        setEditingAssignment(null);
        await fetchAssignments();
        
        if (onAssignmentUpdate) {
          onAssignmentUpdate();
        }
      }
    } catch (error) {
      console.error('Error updating assignment:', error);
      toast.error(error.response?.data?.error || 'Failed to update assignment');
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    try {
      // Parse date string directly to avoid timezone issues
      const dateStr = typeof date === 'string' ? date : date.toISOString();
      const [datePart] = dateStr.split('T');
      const [year, month, day] = datePart.split('-');
      const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return format(localDate, 'MMM dd, yyyy');
    } catch {
      return '-';
    }
  };

  // Get rate display from chamber's unit
  const getRateDisplay = (chamber, rateId) => {
    const unit = chamber?.capacity?.unit;
    const rates = unit?.rates;
    
    if (!rates || !Array.isArray(rates)) {
      return { name: rateId || 'Unknown', rate: 0, unitName: unit?.base_unit || '' };
    }
    
    const rate = rates.find(r => r.rate_id === rateId);
    
    if (!rate) {
      return { name: rateId || 'Unknown', rate: 0, unitName: unit?.base_unit || '' };
    }
    
    return {
      name: rate.display_name || rateId,
      rate: rate.rate || 0,
      basis: rate.rate_basis || 'day',
      unitName: unit?.base_unit || ''
    };
  };

  const totalChamberAssignments = chamberAssignments.reduce(
    (sum, ca) => sum + ca.assignments.length, 0
  );

  const totalCompletedAssignments = completedChamberAssignments.reduce(
    (sum, ca) => sum + ca.assignments.length, 0
  );

  const unitTotals = React.useMemo(() => {
    const totals = {};
    chamberAssignments.forEach(chamberAssign => {
      const unitName = chamberAssign.chamber?.capacity?.unit?.base_unit || 'Units';
      chamberAssign.assignments.forEach(assignment => {
        if (!totals[unitName]) totals[unitName] = 0;
        totals[unitName] += assignment.quantity || 0;
      });
    });
    return totals;
  }, [chamberAssignments]);

  const completedUnitTotals = React.useMemo(() => {
    const totals = {};
    completedChamberAssignments.forEach(chamberAssign => {
      const unitName = chamberAssign.chamber?.capacity?.unit?.base_unit || 'Units';
      chamberAssign.assignments.forEach(assignment => {
        if (!totals[unitName]) totals[unitName] = 0;
        totals[unitName] += assignment.quantity || 0;
      });
    });
    return totals;
  }, [completedChamberAssignments]);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" sx={{ color: '#64748b' }}>Loading assignments...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 2, pt: 1, pb: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={onAssignChambers}
            sx={{
              textTransform: 'none',
              fontSize: '0.8rem',
              borderColor: '#94a3b8',
              color: '#475569',
              backgroundColor: 'transparent',
              '&:hover': { borderColor: '#64748b', backgroundColor: 'rgba(100, 116, 139, 0.06)' }
            }}
          >
            Assign Compartments
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={onAssignResources}
            sx={{
              textTransform: 'none',
              fontSize: '0.8rem',
              borderColor: '#94a3b8',
              color: '#475569',
              backgroundColor: 'transparent',
              '&:hover': { borderColor: '#64748b', backgroundColor: 'rgba(100, 116, 139, 0.06)' }
            }}
          >
            Assign Resources
          </Button>
        </Box>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 36,
            '& .MuiTab-root': {
              minHeight: 36,
              py: 0.5,
              fontSize: '0.8rem',
              textTransform: 'none',
              color: '#64748b',
              backgroundColor: 'transparent'
            },
            '& .MuiTab-root.Mui-selected': { color: '#334155', fontWeight: 600, backgroundColor: 'transparent' },
            '& .MuiTabs-indicator': { backgroundColor: '#64748b' },
            '& .MuiButtonBase-root.MuiTab-root:focus': { color: '#334155', backgroundColor: 'transparent' }
          }}
        >
          <Tab label={`Current Compartments (${totalChamberAssignments})`} />
          <Tab label={`Completed Compartments (${totalCompletedAssignments})`} />
          <Tab label={`Resources (${resourceAssignments.length})`} />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5 }}>
        {tabValue === 0 && (
          <Box>
            {Object.keys(unitTotals).length > 0 && (
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 1, 
                mb: 1.5,
                p: 1,
                backgroundColor: '#f1f5f9',
                borderRadius: 1,
                border: '1px solid #e2e8f0'
              }}>
                <Typography variant="caption" sx={{ fontWeight: 500, mr: 0.5, color: '#64748b' }}>
                  Total:
                </Typography>
                {Object.entries(unitTotals).map(([unitName, total]) => (
                  <Chip
                    key={unitName}
                    label={`${total.toLocaleString()} ${unitName}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 600, height: 22, fontSize: '0.72rem', borderColor: '#94a3b8', color: '#475569' }}
                  />
                ))}
              </Box>
            )}
            {chamberAssignments.length > 0 ? (
              chamberAssignments.map((chamberAssign, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600, color: '#334155' }}>
                    {chamberAssign.chamber.chamber}
                    <Typography component="span" variant="body2" sx={{ ml: 1, color: '#64748b' }}>
                      ({chamberAssign.chamber.capacity?.unit?.base_unit || 'Unit'})
                    </Typography>
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {chamberAssign.assignments.map((assignment, idx) => {
                      const rateInfo = getRateDisplay(chamberAssign.chamber, assignment.rate_id);
                      
                      return (
                        <Card key={assignment._id || idx} variant="outlined" sx={{ 
                          borderRadius: 2,
                          transition: 'all 0.2s',
                          borderColor: '#e2e8f0',
                          textAlign: 'left',
                          '&:hover': {
                            boxShadow: 1,
                            borderColor: '#94a3b8'
                          }
                        }}>
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                              <Box sx={{ flex: 1, textAlign: 'left' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#1e293b', textAlign: 'left' }}>
                                  {rateInfo.name}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#64748b', textAlign: 'left' }}>
                                  {rateInfo.unitName} • {formatRate(rateInfo.rate)}/{rateInfo.basis || 'day'}
                                </Typography>
                                <Chip 
                                  label={assignment.status} 
                                  size="small"
                                  variant="outlined"
                                  sx={{ 
                                    height: 20, 
                                    fontSize: '0.65rem', 
                                    borderColor: assignment.status === 'Active' ? '#86efac' : '#e2e8f0',
                                    color: assignment.status === 'Active' ? '#166534' : '#64748b'
                                  }}
                                />
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditAssignment(chamberAssign, assignment)}
                                    sx={{
                                      p: 0.75,
                                      color: '#64748b',
                                      backgroundColor: 'transparent',
                                      '&:hover': { backgroundColor: 'rgba(100, 116, 139, 0.08)', color: '#475569' }
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Complete">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCompleteAssignment(assignment.chamber_id, assignment._id)}
                                    sx={{
                                      p: 0.75,
                                      color: '#16a34a',
                                      backgroundColor: 'transparent',
                                      '&:hover': { backgroundColor: 'rgba(22, 163, 74, 0.08)', color: '#15803d' }
                                    }}
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteAssignment(
                                      assignment.chamber_id, 
                                      assignment._id,
                                      rateInfo.name
                                    )}
                                    sx={{
                                      p: 0.75,
                                      color: '#94a3b8',
                                      backgroundColor: 'transparent',
                                      '&:hover': { backgroundColor: 'rgba(100, 116, 139, 0.08)', color: '#475569' }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-start' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <InventoryIcon sx={{ fontSize: 14, color: '#64748b' }} />
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                  Quantity: <strong>{assignment.quantity}</strong>
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarIcon sx={{ fontSize: 14, color: '#64748b' }} />
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                  {formatDate(assignment.start_date)} - {assignment.end_date ? formatDate(assignment.end_date) : 'Ongoing'}
                                </Typography>
                              </Box>
                              {assignment.comment && (
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                  <Typography variant="caption" sx={{ color: '#64748b', whiteSpace: 'pre-wrap' }}>
                                    <strong>Note:</strong> {assignment.comment}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                </Box>
              ))
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  No compartment assignments yet. Use the button above to assign.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            {Object.keys(completedUnitTotals).length > 0 && (
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 1, 
                mb: 1.5,
                p: 1,
                backgroundColor: '#f1f5f9',
                borderRadius: 1,
                border: '1px solid #e2e8f0'
              }}>
                <Typography variant="caption" sx={{ fontWeight: 500, mr: 0.5, color: '#64748b' }}>
                  Total:
                </Typography>
                {Object.entries(completedUnitTotals).map(([unitName, total]) => (
                  <Chip
                    key={unitName}
                    label={`${total.toLocaleString()} ${unitName}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 600, height: 22, fontSize: '0.72rem', borderColor: '#94a3b8', color: '#475569' }}
                  />
                ))}
              </Box>
            )}
            {completedChamberAssignments.length > 0 ? (
              completedChamberAssignments.map((chamberAssign, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600, color: '#334155' }}>
                    {chamberAssign.chamber.chamber}
                    <Typography component="span" variant="body2" sx={{ ml: 1, color: '#64748b' }}>
                      ({chamberAssign.chamber.capacity?.unit?.base_unit || 'Unit'})
                    </Typography>
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {chamberAssign.assignments.map((assignment, idx) => {
                      const rateInfo = getRateDisplay(chamberAssign.chamber, assignment.rate_id);
                      
                      return (
                        <Card key={assignment._id || idx} variant="outlined" sx={{ 
                          borderRadius: 2,
                          transition: 'all 0.2s',
                          borderColor: '#e2e8f0',
                          textAlign: 'left',
                          opacity: 0.85,
                          '&:hover': {
                            boxShadow: 1,
                            borderColor: '#94a3b8',
                            opacity: 1
                          }
                        }}>
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                              <Box sx={{ flex: 1, textAlign: 'left' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#1e293b', textAlign: 'left' }}>
                                  {rateInfo.name}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#64748b', textAlign: 'left' }}>
                                  {rateInfo.unitName} • {formatRate(rateInfo.rate)}/{rateInfo.basis || 'day'}
                                </Typography>
                                <Chip 
                                  label={assignment.status} 
                                  size="small"
                                  variant="outlined"
                                  sx={{ 
                                    height: 20, 
                                    fontSize: '0.65rem', 
                                    borderColor: '#e2e8f0',
                                    color: '#64748b'
                                  }}
                                />
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditAssignment(chamberAssign, assignment)}
                                    sx={{
                                      p: 0.75,
                                      color: '#64748b',
                                      backgroundColor: 'transparent',
                                      '&:hover': { backgroundColor: 'rgba(100, 116, 139, 0.08)', color: '#475569' }
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-start' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <InventoryIcon sx={{ fontSize: 14, color: '#64748b' }} />
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                  Quantity: <strong>{assignment.quantity}</strong>
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarIcon sx={{ fontSize: 14, color: '#64748b' }} />
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                  {formatDate(assignment.start_date)} - {assignment.end_date ? formatDate(assignment.end_date) : 'Ongoing'}
                                </Typography>
                              </Box>
                              {assignment.comment && (
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                  <Typography variant="caption" sx={{ color: '#64748b', whiteSpace: 'pre-wrap' }}>
                                    <strong>Note:</strong> {assignment.comment}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                </Box>
              ))
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  No completed compartment assignments.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {tabValue === 2 && (
          <Box>
            {resourceAssignments.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {resourceAssignments.map((assignment, index) => {
                  const resourceName = assignment.resource?.resource || assignment.resource_name || 'Unknown Resource';
                  const description = assignment.resource?.description || '';
                  const unitName = assignment.unit_name || assignment.resource?.unit?.base_unit || assignment.unit_short || '—';
                  const rateLabel = assignment.rate_display_name
                    ? `${assignment.rate_display_name}: ${formatRate(assignment.rate)}${assignment.rate_basis ? ` / ${assignment.rate_basis}` : ''}`
                    : assignment.rate != null
                      ? `${formatRate(assignment.rate)}${assignment.rate_basis ? ` / ${assignment.rate_basis}` : ''}`
                      : '—';
                  const invoiced = assignment.invoiced_quantity ?? 0;
                  const unbilled = assignment.unbilled_quantity ?? Math.max(0, (assignment.quantity ?? 0) - invoiced);
                  return (
                    <Card key={assignment._id || index} variant="outlined" sx={{
                      borderRadius: 2,
                      transition: 'all 0.2s',
                      borderColor: '#e2e8f0',
                      '&:hover': { boxShadow: 1, borderColor: '#94a3b8' }
                    }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#1e293b' }}>
                              {resourceName}
                            </Typography>
                            {description && (
                              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#64748b' }}>
                                {description}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                Unit: <strong>{unitName}</strong>
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                Rate: <strong>{rateLabel}</strong>
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                Quantity: <strong>{assignment.quantity ?? 0}</strong>
                              </Typography>
                              {invoiced > 0 && (
                                <Chip size="small" variant="outlined" icon={<CheckCircleIcon />} label={`Billed: ${invoiced}`} sx={{ height: 20, borderColor: '#86efac', color: '#166534' }} />
                              )}
                              {unbilled > 0 && (
                                <Chip size="small" variant="outlined" label={`Unbilled: ${unbilled}`} sx={{ height: 20, borderColor: '#fde047', color: '#854d0e' }} />
                              )}
                              {invoiced === 0 && unbilled === 0 && (
                                <Typography variant="caption" sx={{ color: '#64748b' }}>—</Typography>
                              )}
                            </Box>
                            {(assignment.usage_periods || []).length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>Usage dates:</Typography>
                                {assignment.usage_periods.map((p, i) => (
                                  <Typography key={p._id || i} variant="caption" component="div" sx={{ color: '#334155', pl: 1 }}>
                                    {formatUsagePeriod(p)}{p.note ? ` · ${p.note}` : ''}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </Box>
                          <Tooltip title="Edit rate, quantity and usage dates">
                            <IconButton
                              size="small"
                              onClick={() => setEditingResource(assignment)}
                              sx={{
                                p: 0.75,
                                color: '#64748b',
                                backgroundColor: 'transparent',
                                '&:hover': { backgroundColor: 'rgba(100, 116, 139, 0.08)', color: '#475569' }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  No resource assignments yet. Use the button above to assign.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <ResourceAssignmentEditDialog
        open={!!editingResource}
        onClose={() => setEditingResource(null)}
        projectId={application?._id}
        assignment={editingResource}
        onSaved={async () => {
          await fetchAssignments();
          if (onAssignmentUpdate) onAssignmentUpdate();
        }}
      />

      {/* Edit Assignment Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Assignment</DialogTitle>
        <DialogContent>
          {editingAssignment && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Compartment: <strong>{editingAssignment.chamber?.chamber}</strong>
              </Typography>
              
              <FormControl fullWidth>
                <InputLabel>Rate</InputLabel>
                <Select
                  value={editForm.rate_id}
                  onChange={(e) => setEditForm(prev => ({ ...prev, rate_id: e.target.value }))}
                  label="Rate"
                >
                  {editingAssignment.chamber?.capacity?.unit?.rates?.map((rate) => (
                    <MenuItem key={rate.rate_id} value={rate.rate_id}>
                      {rate.display_name} ({formatRate(rate.rate)}/{rate.rate_basis || 'day'})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                label="Quantity"
                type="number"
                value={editForm.quantity}
                onChange={(e) => setEditForm(prev => ({ 
                  ...prev, 
                  quantity: e.target.value
                }))}
                inputProps={{ min: 0, step: 'any' }}
                fullWidth
              />
              
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Start Date"
                value={editForm.start_date ? editForm.start_date.toISOString().split('T')[0] : ''}
                onChange={(e) => setEditForm(prev => ({ 
                  ...prev, 
                  start_date: e.target.value ? new Date(e.target.value) : null 
                }))}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                size="small"
                type="date"
                label="End Date (Optional)"
                value={editForm.end_date ? editForm.end_date.toISOString().split('T')[0] : ''}
                onChange={(e) => setEditForm(prev => ({ 
                  ...prev, 
                  end_date: e.target.value ? new Date(e.target.value) : null 
                }))}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label="Comment / Note (optional)"
                value={editForm.comment}
                onChange={(e) => setEditForm(prev => ({ ...prev, comment: e.target.value }))}
                placeholder="Message shown to the project team in their notification and email."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" sx={{ bgcolor: '#475569', '&:hover': { bgcolor: '#334155' } }}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssignmentManagementPane;
